// PartnershipService — SYSTEM_ADMIN partnership management (/system/partnership) + the coach-facing
// marketplace (/admin/marketplace). Migrated to the OpenAPI-generated client:
//   - system/partnership CRUD      -> SystemPartnershipController* SDK fns
//   - admin/marketplace/*          -> MarketplaceController* SDK fns
// Each function keeps its old return shape (callers read res.data / res.data.open) — the adapter
// returns `{data}` and re-throws an axios-shaped `{response:{data,status}}` on error.
//
import {
  systemPartnershipControllerSearch,
  systemPartnershipControllerGetById,
  systemPartnershipControllerCreate,
  systemPartnershipControllerUpdate,
  systemPartnershipControllerDelete,
  systemPartnershipControllerActivate,
  systemPartnershipControllerDeactivate,
  systemPartnershipControllerMarketplacePreview,
  systemPartnershipControllerGetMarketplaceOpen,
  systemPartnershipControllerSetMarketplaceOpen,
  marketplaceControllerList,
  marketplaceControllerStatus,
  marketplaceControllerSubmitLead,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

let partnershipByIdCache = {};

// ─── SYSTEM_ADMIN management ────────────────────────────────────────────────

// Migrated to the OpenAPI client (GET /system/partnership/search). The server now models paging as
// FLAT page/size/sort query params (springdoc @ParameterObject). Returns {data} = the page body,
// preserving the raw-axios contract callers read (res.data.content/totalElements).
export const getPartnerships = async (
  pageId = 0,
  pageSize = 20,
  keyword,
  category,
  enabled
) => {
  const {data, error} = await systemPartnershipControllerSearch({
    query: {
      page: pageId,
      size: pageSize,
      ...(keyword ? {keyword} : {}),
      ...(category !== null && category !== undefined && category !== "" ? {category} : {}),
      ...(enabled !== null && enabled !== undefined ? {enabled} : {}),
    },
  });
  if (error) {
    throwLikeAxios(error);
  }
  return {data};
};

export const getPartnershipById = async (id) => {
  if (!partnershipByIdCache[id]) {
    const {data, error, response} = await systemPartnershipControllerGetById({path: {id}});
    if (error) throwLikeAxios(error, response);
    partnershipByIdCache[id] = {data};
  }
  return partnershipByIdCache[id];
};

export const savePartnership = async (data) => {
  partnershipByIdCache = {};
  const {data: body, error, response} = await systemPartnershipControllerCreate({body: data});
  if (error) throwLikeAxios(error, response);
  return {data: body};
};

export const updatePartnership = async (id, data) => {
  partnershipByIdCache = {};
  const {data: body, error, response} = await systemPartnershipControllerUpdate({path: {id}, body: data});
  if (error) throwLikeAxios(error, response);
  return {data: body};
};

export const deletePartnership = async (id) => {
  partnershipByIdCache = {};
  const {data, error, response} = await systemPartnershipControllerDelete({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const togglePartnershipStatus = async (id, enabled) => {
  partnershipByIdCache = {};
  const fn = enabled ? systemPartnershipControllerActivate : systemPartnershipControllerDeactivate;
  const {data, error, response} = await fn({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

// ─── Coach-facing marketplace ───────────────────────────────────────────────

export const getMarketplacePartnerships = async () => {
  const {data, error, response} = await marketplaceControllerList();
  if (error) throwLikeAxios(error, response);
  return {data};
};

// SYSTEM_ADMIN preview of the marketplace — same consumer cards, but returned regardless of the
// open/closed switch (so the super-admin can review the listing after editing it). No company context.
export const getMarketplacePreview = async () => {
  const {data, error, response} = await systemPartnershipControllerMarketplacePreview();
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const submitMarketplaceLead = async (id, payload) => {
  const {data, error, response} = await marketplaceControllerSubmitLead({
    path: {id},
    body: payload || {},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

// Open/closed status for the current viewer ({ open, canManage }).
export const getMarketplaceStatus = async () => {
  const {data, error, response} = await marketplaceControllerStatus();
  if (error) throwLikeAxios(error, response);
  return {data};
};

// ─── SYSTEM_ADMIN: marketplace open/closed switch ───────────────────────────

export const getMarketplaceOpen = async () => {
  const {data, error, response} = await systemPartnershipControllerGetMarketplaceOpen();
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const setMarketplaceOpen = async (open) => {
  const {data, error, response} = await systemPartnershipControllerSetMarketplaceOpen({query: {open}});
  if (error) throwLikeAxios(error, response);
  return {data};
};
